// Visit-status lifecycle — separate from block_state.reality. Tracks where
// the patient is in the day's flow (front-desk perspective) rather than
// whether the booking itself is real/cancelled/etc.

import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import type { SvgIconComponent } from '@mui/icons-material';
import BlockOutlined from '@mui/icons-material/BlockOutlined';
import HowToRegOutlined from '@mui/icons-material/HowToRegOutlined';
import RadioButtonUncheckedOutlined from '@mui/icons-material/RadioButtonUncheckedOutlined';
import RecordVoiceOverOutlined from '@mui/icons-material/RecordVoiceOverOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';

export type VisitStatus =
  | 'not_started'
  | 'checked_in'
  | 'in_progress'
  | 'checked_out'
  | 'no_show';

export const VISIT_STATUSES: VisitStatus[] = [
  'not_started',
  'checked_in',
  'in_progress',
  'checked_out',
  'no_show',
];

export interface VisitStatusDisplay {
  label: string;
  color: string;
  icon: SvgIconComponent;
  /** Whether this status warrants a visible icon on the calendar block.
   *  `not_started` is the default / quiet state — we skip its icon to avoid
   *  cluttering every event card. */
  showIconOnCard: boolean;
}

const AMBER = '#CA8A04';

const DISPLAY: Record<VisitStatus, VisitStatusDisplay> = {
  not_started: {
    label: 'Not started',
    color: ELIG_TOKENS.inkFaint,
    icon: RadioButtonUncheckedOutlined,
    showIconOnCard: false,
  },
  checked_in: {
    label: 'Checked in',
    color: ELIG_TOKENS.midnight,
    icon: HowToRegOutlined,
    showIconOnCard: true,
  },
  in_progress: {
    label: 'In progress',
    color: AMBER,
    icon: RecordVoiceOverOutlined,
    showIconOnCard: true,
  },
  checked_out: {
    label: 'Checked out',
    color: ELIG_TOKENS.green,
    icon: TaskAltOutlined,
    showIconOnCard: true,
  },
  no_show: {
    label: 'No show',
    color: ELIG_TOKENS.red,
    icon: BlockOutlined,
    showIconOnCard: true,
  },
};

export const getVisitStatusDisplay = (
  status: VisitStatus | null | undefined,
): VisitStatusDisplay => DISPLAY[status ?? 'not_started'];
